/* eslint-disable */

import { useEffect, useState } from 'react'
import spark from '../../assets/PaymentGateway/spark.svg'
import tick from '../../assets/PaymentGateway/Frame 354190.svg'
import { Button } from '../../Components/ui/button'
import { useLocation, useNavigate } from 'react-router-dom'
import { get, post } from '../../http/restservice'
import { Input } from '../../Components/ui/input'
import { AlertCircleIcon, ChevronLeft, FileText, Landmark, ScanLine } from 'lucide-react'
import { useRecoilState, useRecoilValueLoadable } from 'recoil'
import { BankDetails } from '../../store/AccountDetailsStore'

import PaymentStatusModal from '../../Components/modals/OneTimePayment'
import env from '../../env/env'
import MFUpiPayment from './MFUpiPayment'
import PostHogTracker from '../../utils/PostHogTracker'
import { userDetailsState } from '../LoginState'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import {
  BankAccountItem,
  PaymentMethodCard,
} from '../../Components/MutualFunds/PaymentSelectionUI'

const MFPayment = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useRecoilState(userDetailsState)
  const [paymentPayload, setPaymentPayload] = useState(location?.state?.paymentPayload)
  const [orderNumber, setOrderNumber] = useState(location?.state?.orderNumber)
  const clientCode = paymentPayload?.clientCode
  const [upiId, setUpiId] = useState(userDetails?.upiId || '')
  const [isLoading, setIsLoading] = useState(false)
  const bankDetailsLoadable = useRecoilValueLoadable(BankDetails)
  const bankDetails = bankDetailsLoadable.state === 'hasValue' ? bankDetailsLoadable.contents : {}
  const isBanksLoading = bankDetailsLoadable.state === 'loading'
  const isBanksError = bankDetailsLoadable.state === 'hasError'
  const [showPaymentRequestPopup, setShowPaymentRequestPopup] = useState(false)
  const [selectedOption, setSelectedOption] = useState('')
  const [paymentStep, setPaymentStep] = useState('method')
  const [bankQuery, setBankQuery] = useState('')
  const [responseString, setResponseString] = useState('')
  const [modalState, setModalState] = useState({
    isOpen: false,
    status: 'inprogress',
    loading: false,
  })
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const { showError } = useErrorPopup()

  // Approved mandates used to decide whether to show OTM option
  const [approvedMandates, setApprovedMandates] = useState([])

  const [selectedBank, setSelectedBank] = useState(null)

  // Support multiple bank accounts from API response
  const bankAccountsList = (() => {
    const accounts = []

    // If banks array exists, use it (new API format)
    if (Array.isArray(bankDetails?.banks) && bankDetails?.banks.length > 0) {
      bankDetails.banks.forEach((bank) => {
        accounts.push({
          accountNo: bank.accountNo,
          bankName: bank.bankName,
          ifscCode: bank.ifscCode,
          accountHolderName: bank.accountHolderName || bankDetails?.accountHolderName,
        })
      })
    }
    // Fallback to single bank account (backward compatibility)
    else if (bankDetails?.accountNo) {
      accounts.push({
        accountNo: bankDetails.accountNo,
        bankName: bankDetails.bankName,
        ifscCode: bankDetails.ifscCode,
        accountHolderName: bankDetails.accountHolderName,
      })
    }

    return accounts
  })()

  const handlePaymentMode = (option) => {
    if (option !== selectedOption) {
      setSelectedBank(null)
      setBankQuery('')
    }
    setSelectedOption(option)
  }

  const goToDetailsStep = () => {
    if (!selectedOption) return
    window.history.pushState({ fabitsPaymentStep: 'details' }, '')
    setPaymentStep('details')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleHeaderBack = () => {
    if (paymentStep === 'details') {
      window.history.back()
      return
    }
    navigate(-1)
  }

  useEffect(() => {
    const onPopState = () => {
      setPaymentStep('method')
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    if (paymentStep !== 'details') return
    if (bankAccountsList.length === 1 && !selectedBank) {
      setSelectedBank(bankAccountsList[0].accountNo)
    }
  }, [paymentStep, bankDetailsLoadable.state, selectedBank, bankAccountsList.length])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })

    // Track payment method page viewed
    if (orderNumber && paymentPayload?.totalAmount) {
      PostHogTracker.pageViewed('Payment Method Selection', {
        order_number: orderNumber,
        amount: paymentPayload.totalAmount,
      })
    }
  }, [])

  const selectedBankObj = bankAccountsList?.find((b) => b.accountNo === selectedBank) || null
  const activeBankName = (selectedBankObj?.bankName || bankDetails?.bankName || '')?.toUpperCase()
  const activeIfsc = (selectedBankObj?.ifscCode || bankDetails?.ifscCode || '')?.toUpperCase()

  const disableNetBanking =
    activeBankName?.includes('DBS') ||
    activeBankName?.includes('DEVELOPMENT BANK OF SINGAPORE') ||
    activeBankName?.includes('DIGI BANK') ||
    activeIfsc?.startsWith('DBSS')

  const getMandateDetails = async () => {
    try {
      const response = await get('mutualfundservice/api/mutualFund/mandates')

      const approved =
        response?.status === 'SUCCESS' && Array.isArray(response?.data)
          ? response.data.filter((m) => m?.mandateStatus === 'APPROVED')
          : []

      setApprovedMandates(approved)
    } catch (e) {
      setApprovedMandates([])
    }
  }

  useEffect(() => {
    if (!paymentPayload?.totalAmount) return
    getMandateDetails()
  }, [paymentPayload?.totalAmount])

  const shouldShowOTMOption =
    Array.isArray(approvedMandates) &&
    approvedMandates.length > 0 &&
    approvedMandates.some((m) => Number(m?.amount) >= Number(paymentPayload?.totalAmount))

  const filteredBankAccounts = bankAccountsList.filter((bank) => {
    const query = bankQuery.trim().toLowerCase()
    if (!query) return true
    return (
      bank.bankName?.toLowerCase().includes(query) ||
      bank.accountHolderName?.toLowerCase().includes(query) ||
      bank.accountNo?.slice(-4)?.includes(query)
    )
  })
  const showBankSearch = selectedOption === 'NetBanking' && bankAccountsList.length > 2

  const detailsCopy = {
    UPI: {
      title: 'UPI Payment',
      subtitle: 'Select your bank account',
      cta: 'Continue to UPI',
    },
    NetBanking: {
      title: 'Select your bank',
      subtitle: 'Choose the linked account you will pay from',
      cta: 'Continue',
    },
    OTM: {
      title: 'One Time Mandate',
      subtitle: 'Select the account to debit',
      cta: 'Continue',
    },
  }[selectedOption] || {
    title: 'Payment',
    subtitle: 'Select an account',
    cta: 'Continue',
  }

  const canContinueDetails =
    Boolean(selectedBank) && (selectedOption !== 'UPI' || Boolean(upiId?.trim()))

  const handleDetailsContinue = () => {
    if (selectedOption === 'OTM') {
      generateOTP()
      return
    }
    oneTimeOrderPayment()
  }

  useEffect(() => {
    if (!shouldShowOTMOption && selectedOption === 'OTM') {
      setSelectedOption('UPI')
    }
  }, [shouldShowOTMOption, selectedOption])

  const paymentStatus = async () => {
    try {
      const response = await post('/mutualfundservice/api/bseStar/mfUpload/paymentStatus', {
        clientCode: paymentPayload?.clientCode,
        orderNo: orderNumber,
      })

      if (response.isError && !showPaymentRequestPopup) {
        return
      }

      if (
        !response.isError &&
        response.status === 'SUCCESS' &&
        response.data !== 'PAYMENT NOT INITIATED FOR GIVEN ORDER'
      ) {
        // Track payment success
        PostHogTracker.paymentSuccess(orderNumber, paymentPayload?.totalAmount)
        PostHogTracker.investmentConfirmationViewed(
          location?.state?.orders?.schemeCode,
          paymentPayload?.totalAmount,
          'One-Time',
        )

        setShowPaymentRequestPopup(false)
        setModalState({
          isOpen: true,
          status: 'success',
          loading: false,
        })
      }

      if (
        !response.isError &&
        response.status === 'SUCCESS' &&
        response.data === 'PAYMENT NOT INITIATED FOR GIVEN ORDER'
      ) {
        // Track payment failure
        PostHogTracker.paymentFailed(
          orderNumber,
          paymentPayload?.totalAmount,
          'Payment not initiated',
        )

        setShowPaymentRequestPopup(false)
        setModalState({
          isOpen: true,
          status: 'inprogress',
          loading: false,
        })
      }
    } catch (error) {
      console.log('🚀 ~ paymentStatus ~ error:', error)
      PostHogTracker.paymentError(orderNumber, 'polling_error', error.message)
    }
  }

  const oneTimeOrderPayment = async () => {
    try {
      setIsLoading(true)

      // Track payment method selected
      PostHogTracker.paymentMethodSelected(selectedOption, paymentPayload?.totalAmount)

      if (selectedOption === 'UPI' && !upiId) {
        PostHogTracker.formValidationError('Payment', 'upiId', 'UPI ID is required')
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        setIsLoading(false)
        return
      }

      // user must choose which bank account is being used for payment
      // (UPI id is also linked to a specific bank account)
      if (!selectedBank) {
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        setIsLoading(false)
        return
      }

      const payload = { ...paymentPayload, orderNumber: orderNumber }

      if (selectedOption === 'UPI') {
        payload.upiId = upiId
        payload.modeOfPayment = 'UPI'
        payload.loopbackURL = ''
        payload.isFirstTimePaymentUsingUPI = userDetails?.upiId ? false : userDetails?.upiId === upiId ? false : true
        // Track UPI selected
        PostHogTracker.paymentUpiSelected(upiId, paymentPayload?.totalAmount)
      } else if (selectedOption === 'OTM') {
        payload.modeOfPayment = 'OTM'
        payload.loopbackURL = `${env.LOOPBACK_URL}dashboard/mutual-funds/order-payment/status`
      } else {
        payload.modeOfPayment = 'DIRECT'
        payload.loopbackURL = `${env.LOOPBACK_URL}dashboard/mutual-funds/order-payment/status`
        // Track NetBanking selected
        PostHogTracker.paymentNetBankingSelected(bankDetails?.bankName, paymentPayload?.totalAmount)
      }

      payload.accountNo = selectedBank
      payload.bankName = selectedBankObj?.bankName
      payload.ifscCode = selectedBankObj?.ifscCode

      const response = await post(
        '/mutualfundservice/api/bseStar/api/oneTimePayment?newVersion=true',
        payload,
      )

      setIsLoading(false)

      if (response.isError) {
        PostHogTracker.paymentFailed(
          orderNumber,
          paymentPayload?.totalAmount,
          response?.response?.data?.message || 'Payment API failed',
        )
        setModalState({
          isOpen: true,
          status: 'failure',
          loading: false,
        })
        return
      }

      if (response?.status === 'SUCCESS') {
        // Track payment polling started
        PostHogTracker.paymentPollingStarted(orderNumber)
        if (
          response?.status === 'SUCCESS' &&
          !response?.message?.includes('One-time payment will be processed using cheque or otm')
        ) {
          setShowPaymentRequestPopup(true)
        }

        if (
          selectedOption !== 'UPI' &&
          payload.modeOfPayment !== 'UPI' &&
          response?.data?.responsestring
        ) {
          setResponseString(response?.data?.responsestring)

          localStorage.setItem(
            'paymentStatusData',
            JSON.stringify({
              response: response?.data?.responsestring,
              clientCode: paymentPayload?.clientCode,
              orderNo: orderNumber,
              totalAmount: paymentPayload?.totalAmount,
            }),
          )
          navigate('/dashboard/mutual-funds/order-payment/status', {
            state: {
              response: response?.data?.responsestring,
              clientCode: paymentPayload?.clientCode,
              orderNo: orderNumber,
              totalAmount: paymentPayload?.totalAmount,
            },
          })
        }

        if (
          response?.status === 'SUCCESS' &&
          selectedOption === 'OTM' &&
          payload.modeOfPayment === 'OTM' &&
          response?.message?.includes('One-time payment will be processed using cheque or otm')
        ) {
          setModalState({
            isOpen: true,
            status: 'inprogress',
            loading: false,
          })
        }

        if (response?.status === 'FAILURE') {
          PostHogTracker.paymentFailed(
            orderNumber,
            paymentPayload?.totalAmount,
            'Payment request failed',
          )
          setModalState({
            isOpen: true,
            status: 'failure',
            loading: false,
          })
        }
      }
    } catch (error) {
      console.log('🚀 ~ oneTimeOrderPayment ~ error:', error)
    }
  }

  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
    // navigate('/dashboard/mutual-funds/orders')
  }

  const handleModalContinue = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
    navigate('/dashboard/mutual-funds/orders')
  }

  const retryOneTimeOrder = async () => {
    try {
      setIsLoading(true)

      const response = await post(
        'mutualfundservice/api/bseStar/mfOrder/order',
        location?.state?.orders,
      )

      setIsLoading(false)

      if (response?.isError) {
        handleModalClose()
        showError('Order Error', response?.response?.error || 'Failed to retry order.', () => { })
      }

      const orderNumberMatch = response?.data?.match(/ORDER NO: (\d+)/)

      const orderNumbers = orderNumberMatch ? orderNumberMatch[1] : null

      if (response.status === 'SUCCESS' && orderNumbers) {
        handleModalClose()

        const newPaymentPayload = {
          clientCode: clientCode?.toUpperCase(),
          orderNumber: orderNumbers,
          totalAmount: paymentPayload?.totalAmount,
        }

        setPaymentPayload(newPaymentPayload)
        setOrderNumber(orderNumbers)
      } else {
        handleModalClose()
        showError('Order Error', response?.data?.responsestring || 'Order retry failed.', () => { })
      }
    } catch (error) {
      console.log('🚀 ~ retryOneTimeOrder ~ error:', error)
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: userDetails?.phoneNumber,
        email: userDetails?.email,
      },
    )

    if (response.isError) {
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
    }

    if (!response.isError) {
      setOpenOTPPopup(true)
    }
  }

  return (
    <>
      {responseString ? null : (
        <>
          {showPaymentRequestPopup ? (
            <MFUpiPayment
              bankDetails={bankDetails}
              setShowPaymentRequestPopup={setShowPaymentRequestPopup}
              paymentStatus={paymentStatus}
              setModalState={setModalState}
            />
          ) : (
            <div className='bg-white fabits-payment-page'>
              <div className='min-h-screen fabits-payment-shell'>
                <button
                  type='button'
                  className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 bg-white fabits-payment-header w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  onClick={handleHeaderBack}
                >
                  <ChevronLeft className='h-5 w-5' />
                  Payment
                </button>

                <div className='flex items-start justify-center w-full fabits-payment-body'>
                  <div className='w-full max-w-[360px] px-1 fabits-payment-panel'>
                    <h1 className='font-dm-sans text-base font-medium leading-6 text-center fabits-payment-kicker'>
                      Investment amount
                    </h1>
                    <p className='font-dm-sans text-[32px] font-semibold leading-[42px] tracking-[-0.04em] text-center mt-2 mb-8 fabits-payment-amount'>
                      ₹ {paymentPayload?.totalAmount}
                    </p>

                    {paymentStep === 'method' ? (
                      <div className='flex flex-col gap-4 fabits-payment-methods'>
                        <h2 className='font-dm-sans text-base font-semibold leading-6 text-left text-black-500'>
                          How would you like to pay?
                        </h2>

                        <div
                          role='radiogroup'
                          aria-label='How would you like to pay?'
                          className='flex flex-col gap-4'
                        >
                          <PaymentMethodCard
                            id='UPI'
                            selected={selectedOption === 'UPI'}
                            onSelect={handlePaymentMode}
                            icon={
                              <ScanLine
                                className={selectedOption === 'UPI' ? 'text-primary-500' : 'text-[#727272]'}
                              />
                            }
                            title='UPI'
                            supportingText='Fastest · Up to ₹1 Lakh'
                            showFastest
                            sparkIcon={spark}
                          />

                          {!disableNetBanking && (
                            <PaymentMethodCard
                              id='NetBanking'
                              selected={selectedOption === 'NetBanking'}
                              onSelect={handlePaymentMode}
                              icon={
                                <Landmark
                                  className={
                                    selectedOption === 'NetBanking' ? 'text-primary-500' : 'text-[#727272]'
                                  }
                                />
                              }
                              title='Net Banking'
                              supportingText='Higher payment limits'
                            />
                          )}

                          {shouldShowOTMOption && (
                            <PaymentMethodCard
                              id='OTM'
                              selected={selectedOption === 'OTM'}
                              onSelect={handlePaymentMode}
                              icon={
                                <FileText
                                  className={selectedOption === 'OTM' ? 'text-primary-500' : 'text-[#727272]'}
                                />
                              }
                              title='One Time Mandate'
                              supportingText='Higher payment limits'
                            />
                          )}
                        </div>

                        <Button
                          disabled={!selectedOption}
                          className='w-full mt-4 py-3 px-4 rounded-[12px] bg-[#41558d] disabled:bg-primary-300 font-dm-sans text-[14px] font-medium leading-[20px] text-white fabits-payment-proceed'
                          onClick={goToDetailsStep}
                        >
                          Continue
                        </Button>
                      </div>
                    ) : (
                      <div className='flex flex-col gap-6 fabits-payment-methods'>
                        <div className='flex flex-col gap-1'>
                          <h2 className='font-dm-sans text-xl font-semibold leading-7 text-left fabits-payment-option-title text-black-500'>
                            {detailsCopy.title}
                          </h2>
                          <p className='font-dm-sans text-sm font-medium leading-5 text-black-300 fabits-payment-helper'>
                            {detailsCopy.subtitle}
                          </p>
                        </div>

                        {selectedOption === 'OTM' && (
                          <p className='flex items-start gap-2 font-dm-sans text-[13px] font-medium leading-[18px] text-left text-warning-600 py-2'>
                            <AlertCircleIcon className='w-5 h-5 shrink-0 text-warning-600' />
                            <span>
                              Your money will be debited automatically within the next 2 working days.
                            </span>
                          </p>
                        )}

                        {showBankSearch && (
                          <div>
                            <label htmlFor='bank-search' className='sr-only'>
                              Search bank
                            </label>
                            <Input
                              id='bank-search'
                              value={bankQuery}
                              onChange={(e) => setBankQuery(e.target.value)}
                              type='text'
                              placeholder='Search bank'
                              className='fabits-payment-upi-input'
                            />
                          </div>
                        )}

                        {isBanksLoading && (
                          <p className='font-dm-sans text-sm text-black-300 fabits-payment-helper'>
                            Loading linked bank accounts…
                          </p>
                        )}

                        {isBanksError && (
                          <p className='font-dm-sans text-sm text-error-600' role='alert'>
                            Unable to load bank accounts. Please try again.
                          </p>
                        )}

                        {!isBanksLoading && !isBanksError && bankAccountsList.length === 0 && (
                          <p className='font-dm-sans text-sm text-black-300 fabits-payment-helper' role='status'>
                            No linked bank accounts found.
                          </p>
                        )}

                        {!isBanksLoading && bankAccountsList.length > 0 && (
                          <div role='radiogroup' aria-label='Select bank account' className='flex flex-col gap-3'>
                            {filteredBankAccounts.length === 0 ? (
                              <p className='font-dm-sans text-sm text-black-300 fabits-payment-helper'>
                                No banks match your search.
                              </p>
                            ) : (
                              filteredBankAccounts.map((bank) => (
                                <BankAccountItem
                                  key={bank.accountNo}
                                  bank={bank}
                                  isSelected={selectedBank === bank.accountNo}
                                  onSelect={setSelectedBank}
                                  tickIcon={tick}
                                  bankLogosUrl={env.BANK_LOGOS}
                                />
                              ))
                            )}
                          </div>
                        )}

                        {selectedOption === 'UPI' && (
                          <div className='flex flex-col gap-2'>
                            <label
                              htmlFor='upi-id'
                              className='font-dm-sans text-[14px] font-medium leading-[20px] text-black-500 fabits-payment-label'
                            >
                              UPI ID
                            </label>
                            <Input
                              id='upi-id'
                              value={upiId}
                              onChange={(e) => setUpiId(e.target.value)}
                              type='text'
                              placeholder='address@bank'
                              autoComplete='off'
                              className='fabits-payment-upi-input'
                              aria-describedby='upi-limit-note'
                            />
                            <p
                              id='upi-limit-note'
                              className='font-dm-sans text-sm font-medium leading-5 text-black-300 fabits-payment-note'
                            >
                              Some banks may have lower transaction limits. If UPI fails, you can switch to Net Banking.
                            </p>
                          </div>
                        )}

                        <Button
                          disabled={!canContinueDetails}
                          loading={isLoading}
                          className='w-full mt-2 py-3 px-4 rounded-[12px] bg-[#41558d] disabled:bg-primary-300 font-dm-sans text-[14px] font-medium leading-[20px] text-white fabits-payment-proceed'
                          onClick={handleDetailsContinue}
                        >
                          {detailsCopy.cta}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <PaymentStatusModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        onContinue={handleModalContinue}
        onRetry={retryOneTimeOrder}
        status={modalState.status}
        loading={isLoading}
        paymentData={{
          amount: paymentPayload?.totalAmount || '0',
          paymentMode:
            selectedOption === 'UPI'
              ? 'UPI'
              : selectedOption === 'OTM'
                ? 'OTM'
                : 'Internet Banking',
          date: new Date().toLocaleString(),
        }}
      />

      {openOTPPopup && (
        <OrderOtpVerification
          phoneNumber={userDetails?.phoneNumber}
          openOTPPopup={openOTPPopup}
          setOpenOTPPopup={setOpenOTPPopup}
          email={userDetails?.email}
          handleSipOrOT={oneTimeOrderPayment}
        />
      )}
    </>
  )
}

export default MFPayment
